import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiResponse } from '@interfaces/api';
import { LocalstorageService } from '@services/localstorage.service';
import { DEFAULT_ROUTE, LOCALSTORAGE, MODAL_CLASSES } from 'app/core/constants/constants';
import { environment } from 'environments/environment';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import {
  Authenticator,
  ChangePasswordPayload,
  ConfirmAuthenticatorPayload,
  LoginPayload,
  Update2FAPayload,
  User,
} from '../interface/auth';

const apiUrl = `${environment.apiUrl}`;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private localStorage = inject(LocalstorageService);
  private router = inject(Router);
  private dialogService = inject(DialogService);

  public login(payload: LoginPayload): Observable<ApiResponse<User>> {
    return this.http.post<ApiResponse<User>>(`${apiUrl}/auth/login`, {
      ...payload,
      isSite: true,
    });
  }

  public changePassword(
    payload: ChangePasswordPayload,
    token: string,
  ): Observable<ApiResponse<User>> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.post<ApiResponse<User>>(`${apiUrl}/user/change-password`, payload, {
      headers,
    });
  }

  public verify2fa(payload: any): Observable<ApiResponse<User>> {
    return this.http.post<ApiResponse<User>>(`${apiUrl}/auth/verify-2fa`, payload);
  }

  public update2FA(payload: Update2FAPayload): Observable<ApiResponse<{ message: string }>> {
    return this.http.post<ApiResponse<{ message: string }>>(`${apiUrl}/auth/toggle-2fa`, payload);
  }

  public addAuthenticator(): Observable<ApiResponse<Authenticator>> {
    return this.http.post<ApiResponse<Authenticator>>(`${apiUrl}/auth/add-authenticator`, {});
  }

  public confirmAuthenticator(
    payload: ConfirmAuthenticatorPayload,
  ): Observable<ApiResponse<{ message: string }>> {
    return this.http.put<ApiResponse<{ message: string }>>(
      `${apiUrl}/auth/confirm-authenticator`,
      payload,
    );
  }

  public getAuthenticator(): Observable<ApiResponse<Authenticator[]>> {
    return this.http.get<ApiResponse<Authenticator[]>>(`${apiUrl}/auth/get-authenticator`);
  }

  public removeAuthenticator(data: { name: string }): Observable<ApiResponse<{ message: string }>> {
    return this.http.delete<ApiResponse<{ message: string }>>(
      `${apiUrl}/auth/remove-authenticator`,
      {
        body: data,
      },
    );
  }

  public get user() {
    return this.localStorage.getItem(LOCALSTORAGE.USER);
  }

  public get token() {
    return this.localStorage.getItem(LOCALSTORAGE.TOKEN);
  }

  public updateUser<K extends keyof User>(key: K, value: User[K]): void {
    const user = this.user;
    if (!user) return;
    const updatedUser: User = {
      ...user,
      [key]: value,
    };
    this.localStorage.setItem(LOCALSTORAGE.USER, updatedUser);
  }

  public setAuthenticated(user?: User) {
    if (user && user.token) {
      this.localStorage.setItem(LOCALSTORAGE.USER, user);
      this.localStorage.setItem(LOCALSTORAGE.TOKEN, user.token);
      this.localStorage.setItem(LOCALSTORAGE.IS_PASSWORD_CHANGED, user.isPasswordChanged);
      this.router.navigate([DEFAULT_ROUTE]);
    } else {
      this.localStorage.clear();
      this.router.navigate(['/auth/login']);
    }
  }

  public checkForUserPasswordChange(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      const isPasswordChanged =
        this.localStorage.getItem(LOCALSTORAGE.IS_PASSWORD_CHANGED) || false;
      if (!isPasswordChanged) {
        const { ChangePasswordComponent } = await import(
          '../components/auth/change-password/change-password.component'
        );
        const ref = this.dialogService.open(ChangePasswordComponent, {
          header: `Change Password`,
          modal: true,
          styleClass: MODAL_CLASSES,
          data: { ...this.user, isPasswordChanged },
          closeOnEscape: false,
        });

        ref.onClose.subscribe((res) => {
          if (res) {
            this.localStorage.setItem(LOCALSTORAGE.IS_PASSWORD_CHANGED, true);
            resolve(true);
          }
        });
      } else {
        resolve(true);
      }
    });
  }
}
